import React, { useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { TimePeriod } from '../../types/api';
import { useSubscriptionOverallTrend } from '../../hooks/useSubscriptionQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { ChartControls, ChartGuideModal } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { getChartColors, getChartBackgroundColor, getChartJsOptions, formatTooltipLabel, mergeChartOptions } from '../../utils/themeHelpers';
import { formatSubscriptionPercentage, toSubscriptionPercentage } from '../../utils/formatters';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';

interface OverallSubscriptionProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const getPercentageRange = (datasets: any[]) => {
  const values = datasets.flatMap(dataset => dataset.data.filter((value: any) => value !== null && value !== undefined));
  if (!values.length) return { min: 0, max: 105 };

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;

  if (range < 20 && minValue > 20) {
    const padding = Math.max(5, range * 0.2);
    return {
      min: Math.max(0, Math.floor(minValue - padding)),
      max: Math.min(100, Math.ceil(maxValue + padding))
    };
  }

  return { min: 0, max: 105 };
};

const OverallSubscription: React.FC<OverallSubscriptionProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { isModalOpen, currentGuide, openGuide, closeGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data, isLoading, error } = useSubscriptionOverallTrend(branchId, timeRange);

  const componentId = 'overall-subscription';
  const componentTitle = 'روند کلی اشتراک‌گیری';
  const chartColors = getChartColors(currentTheme);

  useEffect(() => {
    if (error && onError) onError(error as Error);
  }, [error, onError]);

  const chartData = (() => {
    if (!data || !data.length) return { labels: [], datasets: [] };

    return {
      labels: data.map(item => item.month),
      datasets: [
        {
          label: 'میانگین اشتراک‌گیری',
          data: data.map(item => toSubscriptionPercentage(item.subscription_percentage)),
          borderColor: chartColors[1],
          backgroundColor: chartColors[1].startsWith('#')
            ? `rgba(${parseInt(chartColors[1].slice(1, 3), 16)}, ${parseInt(chartColors[1].slice(3, 5), 16)}, ${parseInt(chartColors[1].slice(5, 7), 16)}, 0.2)`
            : getChartBackgroundColor(1, currentTheme, 0.2),
          borderWidth: 3,
          pointBackgroundColor: chartColors[1],
          pointBorderColor: currentTheme === 'light' ? '#FFFFFF' : '#27272A',
          pointRadius: 6,
          pointHoverRadius: 10,
          tension: 0.3,
          fill: false
        }
      ]
    };
  })();

  const yAxisRange = getPercentageRange(chartData.datasets);
  const chartOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: !compact },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins.tooltip,
        callbacks: {
          label: (context: any) => formatTooltipLabel(
            context.dataset.label || '',
            formatSubscriptionPercentage(context.parsed.y)
          )
        }
      }
    },
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      y: {
        ...getChartJsOptions(currentTheme).scales?.y,
        min: yAxisRange.min,
        max: yAxisRange.max,
        title: {
          display: !compact,
          text: 'درصد اشتراک‌گیری',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.y?.ticks,
          callback: (value: any) => `${value}%`,
          stepSize: yAxisRange.max - yAxisRange.min < 30 ? 5 : 10,
        }
      },
      x: {
        ...getChartJsOptions(currentTheme).scales?.x,
        title: {
          display: !compact,
          text: 'ماه',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        }
      }
    }
  });

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="animate-pulse">
          <div className="h-6 bg-bg-secondary rounded w-1/3 mb-4"></div>
          <div className="h-80 bg-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3 text-center text-status-error">
        خطا در بارگذاری داده های اشتراک‌گیری
      </div>
    );
  }

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      {!compact && (
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="subscription"
          onInfo={() => openGuide(CHART_GUIDES.overallSubscription)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'subscription',
            componentType: 'chart',
            description: 'نمودار روند کلی اشتراک‌گیری در طول زمان',
            props: { branchId, timeRange, onError, showLoading, compact },
            gridLayout: { containerClass: 'card-base card-padding' }
          })}
          isFavorited={isFavorited(componentId)}
          className="mb-4"
          chartRef={chartRef}
        />
      )}
      <h3 className="title-card">{componentTitle}</h3>
      {data && data.length ? (
        <div className={`mobile-chart-host ${compact ? 'h-60' : 'h-80'}`}>
          <Line data={chartData} options={chartOptions} height={compact ? 240 : 300} />
        </div>
      ) : (
        <div className="h-80 flex items-center justify-center text-text-secondary">داده ای برای نمایش وجود ندارد</div>
      )}

      {currentGuide && !compact && (
        <ChartGuideModal
          isOpen={isModalOpen}
          onClose={closeGuide}
          title={currentGuide.title}
        >
          <div className="space-y-8">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
              <p className="text-text-secondary">{currentGuide.description}</p>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
              <p className="text-text-secondary">{currentGuide.interpretation}</p>
            </div>
            {currentGuide.tips.length > 0 && (
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                <ul className="list-disc list-inside space-y-2">
                  {currentGuide.tips.map((tip, index) => (
                    <li key={index} className="text-text-secondary">{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </ChartGuideModal>
      )}
    </div>
  );
};

export default OverallSubscription;
